import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '@/Redux/reduxStore';
import { useGetStrapiWithdrawalDataQuery } from '@/Redux/Apis/StrapiApi';
import {
  useGetClientDetailsV2Mutation,
  useGetClientHistoryMutation,
  useProcessWithdrawalMutation,
  useGetClientLimitsMutation
} from '@/Redux/Apis/GatewayApi';
import { extractWithdrawalMethods, extractTurnoverText, getWithdrawalMethodById } from '@/Utils/strapiHelpers';
import { useThemeContext } from '@/contexts/ThemeContext';
import { WITHDRAWAL_TOAST_MESSAGES } from '@/constants/toast-messages';
import { getDynatraceLogger } from '@/Services';

// Singleton flag to prevent duplicate API calls across multiple hook instances
let apiCallInProgress = false;
let apiCallCompleted = false;

// Function to reset singleton flags (called on unmount or when modal closes)
export const resetWithdrawalDataState = () => {
  apiCallInProgress = false;
  apiCallCompleted = false;
};

/**
 * Hook for withdrawal data using Redux/RTK Query with Strapi API and Gateway API V2
 * Provides parsed withdrawal methods, turnover text, and client details (split from history)
 */
export const useWithdrawalData = () => {
  // Get auth state - wait for authentication before making API calls
  const { isAuthenticated, isLoading: isAuthLoading } = useSelector((state: RootState) => state.auth);

  // Get client ID and tenant ID from iframe context
  const { clientId: contextClientId, tenantId: contextTenantId } = useThemeContext();

  const clientId = contextClientId || '0';
  const tenantId = contextTenantId || '1';

  // Cleanup on unmount - reset singleton flags
  useEffect(() => {
    // Only cleanup on unmount, not on visibility changes
    return () => {
      resetWithdrawalDataState();
    };
  }, []);

  const {
    data: strapiData,
    isLoading: isStrapiLoading,
    error: strapiError,
    refetch: refetchStrapi
  } = useGetStrapiWithdrawalDataQuery();

  const [
    getClientDetailsV2,
    {
      data: clientDetailsData,
      isLoading: isClientDetailsLoading,
      error: clientDetailsError,
    }
  ] = useGetClientDetailsV2Mutation({
    fixedCacheKey: 'client-details-v2',
  });

  const [
    getClientHistory,
    {
      data: clientHistoryData,
      isLoading: isClientHistoryLoading,
      error: clientHistoryError,
    }
  ] = useGetClientHistoryMutation({
    fixedCacheKey: 'client-history',
  });

  const [
    processWithdrawal,
    {
      isLoading: isProcessingWithdrawal,
      error: processWithdrawalError,
    }
  ] = useProcessWithdrawalMutation();

  const [
    getClientLimits,
    {
      data: clientLimitsData,
      isLoading: isClientLimitsLoading,
    }
  ] = useGetClientLimitsMutation();

  // Fetch client details and history on mount (only if not using dummy data AND authenticated)
  useEffect(() => {
    const shouldProceed = isAuthenticated &&  // Wait for authentication
      !isAuthLoading &&   // Auth is not loading
      tenantId &&
      clientId &&
      !clientDetailsData &&
      !isClientDetailsLoading &&
      !clientDetailsError &&
      !apiCallInProgress &&
      !apiCallCompleted;

    if (!shouldProceed) return;

    // Set flag immediately to prevent other instances from calling
    apiCallInProgress = true;

    const logger = getDynatraceLogger();

    const clientDetailsPromise = getClientDetailsV2({
      tenantId: parseInt(tenantId),
      clientId,
    }).then((result) => {
      if ('error' in result) {
        logger?.logError('Client details mutation failed', result.error as Error);
      }
      return result;
    });

    const clientHistoryPromise = getClientHistory({
      tenantId: parseInt(tenantId),
      clientId,
      page: 1,
      pageSize: 50,
    }).then((result) => {
      if ('error' in result) {
        logger?.logError('Client history mutation failed', result.error as Error);
      }
      return result;
    });

    Promise.allSettled([clientDetailsPromise, clientHistoryPromise]).finally(() => {
      apiCallCompleted = true;
      apiCallInProgress = false;
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, clientId, clientDetailsData, isClientDetailsLoading, isAuthenticated, isAuthLoading]);

  // Parse withdrawal methods from Strapi data
  const withdrawalMethodsFromComet = strapiData ? extractWithdrawalMethods(strapiData) : [];

  // Extract turnover text
  const turnoverText = strapiData ? extractTurnoverText(strapiData) : '';

  // Helper function to get method by ID
  const getMethodById = (methodId: string) => {
    return strapiData ? getWithdrawalMethodById(strapiData, methodId) : undefined;
  };

  // Extract client details data or use dummy data
  const clientDetails = clientDetailsData?.data;

  const bankDetails = clientDetails?.bankDetails || null;
  const withdrawalMethods = clientDetails?.withdrawalMethods || [];
  const withdrawalMethodsEnabled = clientDetails?.withdrawalMethodsMinMaxAmounts || [];
  const minMaxAmounts = clientDetails?.withdrawalMethodsMinMaxAmounts || [];

  // Extract withdrawal history from separate endpoint (returns array directly)
  const withdrawalHistoryRecords = clientHistoryData?.data;

  // Helper function to get limits by payment provider name
  const getLimitsByMethodName = (methodName: string) => {
    // Normalize the method name for better matching
    const normalizedMethodName = methodName.toLowerCase().replace(/[\s-]/g, '');

    // Try exact match first
    let limits = minMaxAmounts.find(
      (limit) => limit.paymentProviderName.toLowerCase() === methodName.toLowerCase()
    );

    // If not found, try normalized match (remove spaces and hyphens)
    limits ??= minMaxAmounts.find(
      (limit) => limit.paymentProviderName.toLowerCase().replace(/[\s-]/g, '') === normalizedMethodName
    );

    // If still not found, try partial match
    limits ??= minMaxAmounts.find(
      (limit) => {
        const normalizedProviderName = limit.paymentProviderName.toLowerCase().replace(/[\s-]/g, '');
        return normalizedProviderName.includes(normalizedMethodName) ||
          normalizedMethodName.includes(normalizedProviderName);
      }
    );

    return {
      minimum: limits?.minimum || 0,
      maximum: limits?.maximum || 0,
      dailyLimit: limits?.dailyLimit || 0,
      monthlyLimit: limits?.monthlyLimit || 0,
    };
  };

  // Helper function to get remaining limits from a specific withdrawal method
  const getRemainingLimitsByMethodId = (methodId: number | string) => {
    const id = typeof methodId === 'string' ? parseInt(methodId) : methodId;
    const method = withdrawalMethods.find(wm => wm.id === id);

    if (method?.remainingWithdrawalLimit) {
      return {
        remainingDailyLimit: method.remainingWithdrawalLimit.remainingDailyLimit,
        remainingMonthlyLimit: method.remainingWithdrawalLimit.remainingMonthlyLimit,
        dailyLimit: method.remainingWithdrawalLimit.dailyLimit,
        monthlyLimit: method.remainingWithdrawalLimit.monthlyLimit,
      };
    }

    return {
      remainingDailyLimit: 0,
      remainingMonthlyLimit: 0,
      dailyLimit: 0,
      monthlyLimit: 0,
    };
  };

  // Helper function to get remaining limits by method name
  const getRemainingLimitsByMethodName = (methodName: string) => {
    // First check if we have clientLimitsData from the new API
    if (clientLimitsData?.success && clientLimitsData?.data) {
      return {
        remainingDailyLimit: clientLimitsData.data.remainingDailyLimit,
        remainingMonthlyLimit: clientLimitsData.data.remainingMonthlyLimit,
        dailyLimit: clientLimitsData.data.dailyLimit,
        monthlyLimit: clientLimitsData.data.monthlyLimit,
      };
    }

    // Fallback to the old method if new API data not available
    const methodId = getWithdrawalMethodId(methodName);
    if (methodId) {
      return getRemainingLimitsByMethodId(methodId);
    }

    return {
      remainingDailyLimit: 0,
      remainingMonthlyLimit: 0,
      dailyLimit: 0,
      monthlyLimit: 0,
    };
  };

  // Helper function to fetch client limits for a specific method
  const fetchClientLimitsByMethodName = async (methodName: string) => {
    const methodId = getWithdrawalMethodId(methodName);
    if (!methodId) {
      return null;
    }

    try {
      const result = await getClientLimits({
        tenantId: parseInt(tenantId),
        clientId,
        clientTransactionId: methodId,
      }).unwrap();

      return result;
    } catch (error) {
      const logger = getDynatraceLogger();
      logger?.logError('Failed to fetch client limits', error as Error);
      return null;
    }
  };

  // Helper function to check if a withdrawal method is offline
  const isWithdrawalMethodOffline = (methodName: string): boolean => {
    // Check if method is forced offline via env variable (for testing)
    const forceOfflineMethods = import.meta.env.VITE_FORCE_OFFLINE_METHODS?.split(',').map((m: string) => m.trim()) || [];
    const isForcedOffline = forceOfflineMethods.some((m: string) =>
      m.toLowerCase() === methodName.toLowerCase()
    );

    if (isForcedOffline) {
      return true;
    }

    // Find the withdrawal method by ID or message name only
    // Avoid matching on description as it's a shared category label (e.g. "Cash Send Withdrawal")
    // which would cause false positives across multiple methods
    const method = withdrawalMethodsEnabled.find(wm => wm.paymentProviderName.toString() === methodName) ||
      withdrawalMethodsEnabled.find(wm => wm.description?.toLowerCase() === methodName.toLowerCase());

    return method ? !method.enabled : false;
  };

  // Helper function to get withdrawal method ID from ClientDetails
  const getWithdrawalMethodId = (methodName: string): number | null => {

    // First try to find in withdrawalMethods if available (backward compatibility)
    if (withdrawalMethods.length > 0) {
      const method = withdrawalMethods.find(wm =>
        wm.id.toString() === methodName ||
        wm.message.toLowerCase() === methodName.toLowerCase()
      );
      if (method) {
        return method.id;
      }
    }

    // Fallback to withdrawalMethodsMinMaxAmounts using paymentProviderName
    const normalizedMethodName = methodName.toLowerCase().replace(/[\s-]/g, '');

    // Try exact match first
    let limit = minMaxAmounts.find(
      (limit) => limit.paymentProviderName.toLowerCase() === methodName.toLowerCase()
    );

    // If not found, try normalized match (remove spaces and hyphens)
    limit ??= minMaxAmounts.find(
      (limit) => limit.paymentProviderName.toLowerCase().replace(/[\s-]/g, '') === normalizedMethodName
    );

    // If still not found, try partial match
    limit ??= minMaxAmounts.find(
      (limit) => {
        const normalizedProviderName = limit.paymentProviderName.toLowerCase().replace(/[\s-]/g, '');
        return normalizedProviderName.includes(normalizedMethodName) ||
          normalizedMethodName.includes(normalizedProviderName);
      }
    );

    if (limit) {
      return limit.paymentlProviderLimitsId;
    }

    return null;
  };

  // Helper function to get withdrawal method ID by Strapi method ID
  const getWithdrawalMethodIdByStrapiId = (strapiMethodId: string): number | null => {
    // Map Strapi method IDs to API method names
    const strapiToApiMapping: Record<string, string> = {
      'eft': 'EFT',
      'cashsend': 'ABSA Cash Send',
      'instantmoney': 'Instant Money',
      'sendimalimobimoney': 'Nedbank iMali',
      'ewallet': 'eWallet',
      'vouchers': '1Voucher',
      'wallets': '1ForYou',
      'cashvouchers': 'Eezi Cash',
    };

    const apiMethodName = strapiToApiMapping[strapiMethodId.toLowerCase()];
    if (!apiMethodName) return null;

    return getWithdrawalMethodId(apiMethodName);
  };

  // Helper function to get min/max amounts by withdrawal method ID
  const getMinMaxAmountsByMethodId = (methodId: number | string): { minimum: number; maximum: number } | null => {
    const id = typeof methodId === 'string' ? parseInt(methodId) : methodId;

    // Find the limits by matching paymentlProviderLimitsId with the withdrawal method id
    const limits = minMaxAmounts.find(
      (limit) => limit.paymentlProviderLimitsId === id
    );

    if (limits) {
      return {
        minimum: limits.minimum,
        maximum: limits.maximum,
      };
    }

    return null;
  };

  // Helper function to get min/max amounts by method name (for backward compatibility)
  const getMinMaxAmountsByMethodName = (methodName: string): { minimum: number; maximum: number } | null => {
    // First, get the withdrawal method ID
    const methodId = getWithdrawalMethodId(methodName);

    if (methodId) {
      return getMinMaxAmountsByMethodId(methodId);
    }

    return null;
  };

  // Map child item names to API message field names
  const mapChildNameToApiName = (childName: string): string => {
    const mapping: Record<string, string> = {
      // Vouchers
      '1Voucher Pin': '1Voucher',
      'Mr D Voucher': 'Mr D',
      'Mr D': 'Mr D',
      'Takealot Voucher': 'Takealot',
      'Takealot': 'Takealot',
      'OTT Voucher': 'OTT Voucher',
      'OTT': 'OTT Voucher',
      'OTTBLU': 'OTT Voucher',
      'BLU Voucher': 'BLU Voucher',
      'BLU': 'BLU Voucher',
      // Wallets
      '1Voucher App': '1ForYou',
      '1ForYou': '1ForYou',
      'Spot Money': 'Spot Money',
      'SpotMoney': 'Spot Money',
      'ShopriteMMA': 'ShopriteMMA',
      'Shoprite MMA': 'ShopriteMMA',
      'MoyaPay': 'MoyaPay',
      'Moya Pay': 'MoyaPay',
      'MTNMoMo': 'MTNMoMo',
      'MTN Momo': 'MTNMoMo',
      'MTN MOMO': 'MTNMoMo',
      'MTN': 'MTNMoMo',
      // Cash Vouchers
      'EEZI Cash Voucher': 'Eezi Cash',
      'Eezi Cash': 'Eezi Cash',
      'EEZI': 'Eezi Cash',
      'Shoprite Cash Pin': 'Shoprite Cash Pin',
      'Shoprite Cash': 'Shoprite Cash Pin',
      'Shoprite Cash Voucher': 'Shoprite Cash Pin',
    };
    return mapping[childName] || childName;
  };

  // Helper function to check if all children of a method are offline
  const areAllChildrenOffline = (methodId: string): boolean => {
    const voucherTypes = ['vouchers', 'wallets', 'cashvouchers'];
    if (!voucherTypes.includes(methodId.toLowerCase())) {
      return false;
    }

    const methodData = strapiData ? getWithdrawalMethodById(strapiData, methodId) : undefined;
    if (!methodData?.children || methodData.children.length === 0) {
      return false;
    }

    // Check if ALL children are offline
    return methodData.children.every((child) => {
      const apiName = mapChildNameToApiName(child.name);
      return isWithdrawalMethodOffline(apiName);
    });
  };

  const balance = {
    available: clientDetails?.availableBalance || 0,
    withdrawable: clientDetails?.availableBalance || 0,
    bonus: clientDetails?.bonusAmount || 0,
  };

  // Get reference (cell phone) from client details
  const cellPhoneReference = clientDetails?.cellphone || 'N/A';
  const clientIdReference = clientId;

  // Submit withdrawal function
  const submitWithdrawal = async (amount: number) => {
    try {
      const result = await processWithdrawal({
        action: 64,
        request: 14,
        payload: {
          actionsResource: 22,
          TenantId: parseInt(tenantId),
          ClientID: parseInt(clientId),
          WithdrawalAmount: amount,
        },
      }).unwrap();

      // Check for error responses that should redirect to error page
      // Case 1: responsePayload is null with status 200
      // This could mean: validation passed with no data to return, OR it could be an error
      // We'll treat it as success to allow proceeding, unless there's evidence it's an error
      if (result?.responsePayload === null && result?.status === 200) {
        // If responsePayload is null but status is 200, treat as validation success
        return {
          success: true,
          message: 'Withdrawal validation passed'
        };
      }

      // Case 2: responseType -1 generally indicates an error, except when responseMessage is exactly "false"
      if (result?.responsePayload?.responseType === -1) {
        // Only accept as success if responseMessage is exactly "false" (validation passed)
        if (result?.responsePayload?.responseMessage === 'false') {
          return { success: true, message: 'Withdrawal validation passed' };
        }

        // Any other responseMessage with responseType -1 is an error
        // This includes "An error has occurred. Please try again later" or other error messages
        return {
          success: false,
          shouldRedirectToError: true,
          message: result?.responsePayload?.responseMessage || 'Encountered an error while processing your request'
        };
      }

      // Case 3: Check if responseMessage is "true" (as string) - success
      if (result?.responsePayload?.responseMessage === 'true') {
        return { success: true, message: 'Withdrawal submitted successfully' };
      }

      // Case 4: If we get here with status 200, assume success
      if (result?.status === 200) {
        return { success: true, message: 'Withdrawal validated successfully' };
      }

      // Default case: treat as error
      const errorMessage = result?.responsePayload?.responseMessage || WITHDRAWAL_TOAST_MESSAGES.withdrawalProcessingError;
      return {
        success: false,
        message: errorMessage
      };
    } catch (error) {
      const logger = getDynatraceLogger();
      logger?.logError('Withdrawal submission error', error as Error);
      // Use toast message constant for validation error
      return {
        success: false,
        message: WITHDRAWAL_TOAST_MESSAGES.withdrawalValidationError
      };
    }
  };

  return {
    // Strapi data
    strapiData,
    withdrawalMethodsFromComet,
    turnoverText,

    // Gateway API data
    clientDetails,
    bankDetails,
    withdrawalMethods,
    minMaxAmounts,
    balance,
    canClientWithdraw: clientDetails?.canClientWithdraw || false,

    // Syx client data
    cellPhoneReference,
    clientIdReference,

    // States
    isLoading: isStrapiLoading || ((isClientDetailsLoading || isClientHistoryLoading)),
    error: strapiError || ((clientDetailsError || clientHistoryError)),
    isProcessingWithdrawal,
    processWithdrawalError,

    // Withdrawal history
    withdrawalHistoryRecords,
    isHistoryLoading: isClientHistoryLoading,
    historyError: clientHistoryError,

    // Actions
    refetchStrapi,
    refetchClientDetails: () => {
      return Promise.all([
        getClientDetailsV2({
          tenantId: parseInt(tenantId),
          clientId
        }),
        getClientHistory({
          tenantId: parseInt(tenantId),
          clientId,
          page: 1,
          pageSize: 50
        })
      ]);
    },
    refetchClientHistory: (page = 1, pageSize = 50) => {
      return getClientHistory({
        tenantId: parseInt(tenantId),
        clientId,
        page,
        pageSize
      });
    },
    getMethodById,
    getLimitsByMethodName,
    getRemainingLimitsByMethodId,
    getRemainingLimitsByMethodName,
    isWithdrawalMethodOffline,
    getWithdrawalMethodId,
    getWithdrawalMethodIdByStrapiId,
    getMinMaxAmountsByMethodId,
    getMinMaxAmountsByMethodName,
    areAllChildrenOffline,
    submitWithdrawal,
    fetchClientLimitsByMethodName,
    clientLimitsData,
    isClientLimitsLoading,

    // Legacy mock data
    withdrawalData: null,
  };
};
